import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip } from "lucide-react";
import { Chip } from "../ui";
import type { Task } from "../core/schema";
import { useSession } from "../app/session";

/**
 * Attachments (spec §9.21): bytes go to the blob store through the API, the doc keeps the reference.
 * Paste or drop onto the section, or pick a file. Images render inline; everything else is a chip.
 */
export function Attachments({
  task,
  workspaceId,
  readOnly,
}: {
  task: Task;
  workspaceId: string;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const features = useSession((s) => s.session?.features);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!features?.attachments) return null;
  const base = `/api/v1/workspaces/${workspaceId}/tasks/${task.id}/attachments`;

  async function upload(files: FileList | File[]) {
    setError(null);
    setBusy(true);
    for (const file of Array.from(files)) {
      if (file.size > (features?.attachmentMaxMb ?? 25) * 1024 * 1024) {
        setError(t("errors.attachments.tooLarge", { n: features?.attachmentMaxMb ?? 25 }));
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(base, { method: "POST", body: form, credentials: "same-origin" });
      if (!res.ok)
        setError(
          t(`errors.${((await res.json()) as { error: string }).error}`, {
            defaultValue: t("error.generic"),
            n: features?.attachmentMaxMb ?? 25,
          }),
        );
    }
    setBusy(false);
  }

  const items = Object.entries(task.attachments ?? {});
  return (
    <section
      className="border-t border-neutral-200 pt-2 dark:border-neutral-800"
      data-testid="attachments"
      onPaste={(e) => !readOnly && e.clipboardData.files.length && void upload(e.clipboardData.files)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (!readOnly && e.dataTransfer.files.length) void upload(e.dataTransfer.files);
      }}
    >
      <div className="mb-1 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
          {t("attachments.title")}
        </h3>
        {!readOnly && (
          <>
            <input
              ref={input}
              type="file"
              multiple
              className="sr-only"
              aria-label={t("attachments.add")}
              data-testid="attachment-file"
              onChange={(e) => e.target.files && void upload(e.target.files)}
            />
            <button
              type="button"
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1 text-[12px] text-accent hover:underline"
              onClick={() => input.current?.click()}
            >
              <Paperclip size={12} aria-hidden /> {t("attachments.add")}
            </button>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map(([id, a]) => {
          const href = `/blobs/${a.sha256}?name=${encodeURIComponent(a.name)}`;
          const remove = readOnly
            ? undefined
            : () => void fetch(`${base}?id=${id}`, { method: "DELETE", credentials: "same-origin" });
          return a.mime.startsWith("image/") ? (
            <figure key={id} className="w-full" data-testid="attachment-image">
              <img
                src={href}
                alt={a.name}
                className="max-h-64 rounded-md border border-neutral-200 dark:border-neutral-800"
              />
              <figcaption className="mt-1">
                <Chip onRemove={remove} removeLabel={t("common.remove")}>
                  <a href={href} className="hover:underline">
                    {a.name}
                  </a>
                </Chip>
              </figcaption>
            </figure>
          ) : (
            <Chip key={id} onRemove={remove} removeLabel={t("common.remove")} data-testid="attachment-chip">
              <Paperclip size={12} aria-hidden />
              <a href={href} className="hover:underline">
                {a.name}
              </a>
              <span className="font-mono text-[11px] text-neutral-500">
                {Math.max(1, Math.round(a.size / 1024))} kB
              </span>
            </Chip>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-[12.5px] text-err">
          {error}
        </p>
      )}
    </section>
  );
}
